import { useViewportStore } from "@/stores/viewport/index.ts";
import { expandMediaCollection } from "@/util/mediaHelper.v2.ts";

/*
The complete set of colors used by the site.
*/
const palette = (state, getters) => {
  /*
  First we get all color values
   */
  const values = [
    "primaryColor",
    "primaryColorDark",
    "primaryColorLight",
    "backgroundColor",
    "bannerBodyBackgroundColor",
    "bannerBodyTextColor",
    "bannerHeaderBackgroundColor",
    "bannerStripeColor",
    "bannerTitleColor",
    "categoryLogoCardBackground",
    "categoryLogoCardHeaderColor",
    "categoryLogoCardStripe",
    "categoryLogoCardText",
    "categoryTextColor",
    "headerStripeColor",
    "homeButtonsColor",
    "personStripeTextColor",
    "startColor",
    "titleColor",
    "pillCategoryTextColor",
  ]
    .map((varName) =>
      getters.getStoreVar("Settings", "styling", "color", varName)
    )
    .sort();
  /*
  We return a de-duped array with only valid values.
   */
  return [...new Set(values.filter((item) => item != null && item !== ""))];
};

export default {
  getClientID: (state) => state.clientID,
  getTouchScreen: (state) => state.touchScreen,
  getAccessible: (state) => state.accessible,
  getNoTouch: (state) => state.noTouch,
  getAppInitialized: (state) => state.appInitialized,
  getCategoriesURLQueries: (state) => state.categoriesURLQueries,
  getStyleID: (state) => state.styleID,
  isAuth: (state) => state.isAuth,
  isActivated: (state) => state.isActivated,
  getStyleName: (state) => state.styleName,
  getIsAdmin: (state) => state.isAdmin,
  getIsInternal: (state) => state.isInternal,
  getHasSignedAgreements: (state) => state.hasSignedAgreements,
  getSiteIds: (state) => state.siteIds,
  getUserEmail: (state) => state.userEmail,
  getIsSaving: (state) => state.isSaving,
  // generic getter for all store variables that need to be accessed by pages
  getStoreVar: (state) => (page, group, type, name) => {
    if (
      page in state &&
      group in state[page] &&
      type in state[page][group] &&
      name in state[page][group][type]
    ) {
      return state[page][group][type][name];
    }
    return undefined;
  },
  // generic getter for all store type groupings that need to be accessed by pages
  getStoreType: (state) => (page, group, type) => state[page][group][type],
  // generic getter for all store groups that need to be accessed by pages
  getStoreGroup: (state) => (page, group) => state[page][group],
  getStorePage: (state) => (page) => state[page],
  getSiteNavigatorVar: (state) => (key) => state.SiteNavigator[key],
  getSettingsVar: (state) => (key) => state.Settings[key],
  getIsMobileDrawerOpen: (state) => state.isMobileDrawerOpen,
  getSelectedHomeButton: (state) => state.selectedHomeButton,
  getServerErrorMessage: (state) => state.serverErrorMessage,
  getSiteBuilderFormVar: (state) => (key) => state.SiteBuilderForm[key],
  getIsSiteBuilderFormOpen: (state) => state.isSiteBuilderFormOpen,
  /*
   Returns a font family value from the site fonts (1, 2, 3)
   For use in CSS e.g. font-family: v-bind(store.getFontFamily(1))

   type: integer 0 < type <= 3
   */
  getFontFamily: (state) => (type) => {
    if (![1, 2, 3].includes(type)) {
      console.log("getFont argument should be 1, 2, or 3");
      return null;
    }
    const font = ["font", "font2", "font3"][type - 1];
    return `'${(state.Settings.styling.text[font] ?? "").split(".")[0]}'`;
  },
  // expands media collection if it has grouped sub-items
  // e.g. a PDF with images.
  getExpandedMedia: (state, getters) => (page, group, type, name) =>
    expandMediaCollection(getters.getStoreVar(page, group, type, name)),
  showCMS: (state, getters) => {
    const viewportStore = useViewportStore();
    return (
      state.isAuth &&
      state.showCMS &&
      !getters.noCMSPage &&
      !viewportStore.isMobile &&
      navigator.onLine
    );
  },
  getOrganizationName: (state) => state.organizationName,
  getDefaultSiteID: (state) => state.defaultSiteId,
  palette,
  siteConfig: (state) => ({
    Home: state.Home,
    Categories: state.Categories,
    Category: state.Category,
    Person: state.Person,
    SubCategories: state.SubCategories,
    Settings: state.Settings,
  }),
  noCMSPage: (state) =>
    ["BulkCategoryEditor", "BulkRecordItemsEditor", "RecentlyDeleted"].includes(
      state.route.name
    ),
  getSearchResults: (state) => state.searchResults,
  getSearchQuery: (state) => state.searchQuery,
  getRoutePending: (state) => state.isRoutePending,
  getCsDetails: (state) => state.csDetails,
};
